import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { Suspense, lazy } from "react";
import { publicRoutes } from "./public";
import { authenticatedRoutes } from "./authenticated";
import adminRoutes from "./admin";
import { MainLayout } from "../layouts/MainLayout";
import { AuthLayout } from "../layouts/AuthLayout";
import { PublicLayout } from "../layouts/PublicLayout";
import AdminGuard from "../components/Guards/AdminGuard";

// Lazy load the NotFound component
const NotFound = lazy(() => import("../pages/Error/NotFound"));
const PublicCollectionDetail = lazy(() => import("../pages/Public/PublicCollectionDetail"));
const PublicProfile = lazy(() => import("../pages/Public/PublicProfile"));

// Redirect component for legacy /public URLs
const PublicToDirectoryRedirect = () => {
  const { uuid, domain } = useParams();
  return <Navigate to={`/directory/${uuid}/${domain}`} replace />;
};

const LoadingFallback = () => (
  <div
    className="d-flex justify-content-center align-items-center"
    style={{ minHeight: "200px" }}
  >
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public authentication routes */}
      <Route element={<AuthLayout />}>
        {publicRoutes.map(({ path, Component }) => (
          <Route
            key={path}
            path={path}
            element={
              <Suspense fallback={<LoadingFallback />}>
                <Component />
              </Suspense>
            }
          />
        ))}
      </Route>

      {/* Directory collection routes (new clean URLs) */}
      <Route element={<PublicLayout />}>
        <Route
          path="/directory/:uuid/:domain"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <PublicCollectionDetail />
            </Suspense>
          }
        />
        {/* Public "channel" page: all of a user's public collections */}
        <Route
          path="/u/:userUuid"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <PublicProfile />
            </Suspense>
          }
        />
      </Route>

      {/* Legacy /public URLs - redirect to /directory */}
      <Route
        path="/public/:uuid/:domain"
        element={<PublicToDirectoryRedirect />}
      />

      {/* Protected routes */}
      <Route element={<MainLayout />}>
        {authenticatedRoutes.map(({ path, Component }) => (
          <Route
            key={path}
            path={path}
            element={
              <Suspense fallback={<LoadingFallback />}>
                <Component />
              </Suspense>
            }
          />
        ))}
      </Route>

      {/* Admin routes - protected by AdminGuard */}
      <Route
        path={adminRoutes.path}
        element={
          <AdminGuard>
            <Suspense fallback={<LoadingFallback />}>
              {adminRoutes.element}
            </Suspense>
          </AdminGuard>
        }
      >
        {adminRoutes.children.map((route) => (
          <Route
            key={route.path || 'admin-index'}
            index={route.index}
            path={route.path}
            element={
              <Suspense fallback={<LoadingFallback />}>
                {route.element}
              </Suspense>
            }
          />
        ))}
      </Route>

      <Route
        path="*"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <NotFound />
          </Suspense>
        }
      />
    </Routes>
  );
}

export default AppRoutes;
